import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlusCircle, Edit, Trash2, Eye, MapPin, Dog } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getMyListings()
      .then(r => setListings(r.data.listings || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    try {
      await api.deleteListing(id);
      setListings(prev => prev.filter(l => l.listing_id !== id));
      toast.success('Listing deleted');
    } catch {
      toast.error('Failed to delete listing');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="dashboard-page">
      <SEO
        title="My Dashboard"
        description="Manage your dog listings on DoggoHub. Create, edit, and track your dog sale listings."
      />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Nunito'] font-extrabold text-3xl text-[#1A1D20] tracking-tight">
            My Dashboard
          </h1>
          <p className="text-sm text-[#5C636A] mt-1">Welcome back, {user?.name}</p>
        </div>
        <Button
          onClick={() => navigate('/dashboard/new')}
          className="rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-semibold shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
          data-testid="dashboard-new-listing"
        >
          <PlusCircle className="w-4 h-4 mr-1" /> New Listing
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <p className="text-xs text-[#5C636A] font-medium">Total Listings</p>
          <p className="font-['Nunito'] font-extrabold text-3xl text-[#1A1D20] mt-1">{listings.length}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <p className="text-xs text-[#5C636A] font-medium">Active</p>
          <p className="font-['Nunito'] font-extrabold text-3xl text-[#7ED321] mt-1">
            {listings.filter(l => l.status === 'active').length}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <p className="text-xs text-[#5C636A] font-medium">Total Views</p>
          <p className="font-['Nunito'] font-extrabold text-3xl text-[#4A90E2] mt-1">
            {listings.reduce((sum, l) => sum + (l.views || 0), 0)}
          </p>
        </div>
      </div>

      {/* Listings */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-['Nunito'] font-bold text-lg text-[#1A1D20]">My Listings</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-[#5C636A]">Loading...</div>
        ) : listings.length > 0 ? (
          <div className="divide-y divide-gray-50">
            {listings.map(listing => (
              <div key={listing.listing_id} className="p-4 sm:p-6 flex flex-col sm:flex-row items-start gap-4 hover:bg-gray-50/50 transition-colors" data-testid={`dashboard-listing-${listing.listing_id}`}>
                <div className="w-full sm:w-20 h-20 rounded-xl overflow-hidden shrink-0 bg-gray-100">
                  <img
                    src={listing.images?.[0] ? api.getFileUrl(listing.images[0]) : 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=200'}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-[#1A1D20] text-sm">{listing.title}</h3>
                      <p className="text-xs text-[#5C636A] mt-0.5">{listing.breed} &middot; {listing.age}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm font-bold text-[#F5A623]">${listing.price?.toLocaleString()}</span>
                        <span className="text-xs text-[#5C636A] flex items-center gap-0.5">
                          <MapPin className="w-3 h-3" /> {listing.location}
                        </span>
                        <span className="text-xs text-[#5C636A] flex items-center gap-0.5">
                          <Eye className="w-3 h-3" /> {listing.views || 0} views
                        </span>
                      </div>
                    </div>
                    <Badge className={`shrink-0 rounded-full text-xs border-0 ${listing.status === 'active' ? 'bg-green-50 text-[#7ED321]' : 'bg-gray-100 text-[#5C636A]'}`}>
                      {listing.status}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/dashboard/edit/${listing.listing_id}`)}
                    className="rounded-lg"
                    data-testid={`edit-listing-${listing.listing_id}`}
                  >
                    <Edit className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(listing.listing_id)}
                    className="rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-listing-${listing.listing_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Dog className="w-12 h-12 text-[#5C636A] mx-auto mb-3 opacity-40" />
            <p className="text-[#5C636A] mb-4">No listings yet</p>
            <Button
              onClick={() => navigate('/dashboard/new')}
              className="rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-semibold cta-button"
              data-testid="empty-state-new-listing"
            >
              <PlusCircle className="w-4 h-4 mr-1" /> Create Your First Listing
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
